import * as React from 'react';
import GiftSuggestions from "./GiftSuggestions";
import GiftForm from "./GiftForm";
import GiftFormValues, {
    Gender,
    GetEnumKeyByEnumValue,
    IsGender,
    IsOccasion,
    IsRelationship,
    Occasion,
    Relationship,
    ToGender
} from "./GiftFormValues";
import api from "../api/api";
import {GiftResponse} from "../api/GiftResponse";
import {CircularProgress, Container, Paper} from "@mui/material";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import Grid from "@mui/material/Unstable_Grid2";
import Grid2 from "@mui/material/Unstable_Grid2";
import Button from "@mui/material/Button";
import useAnalyticsEventTracker, {FORM_SUBMIT, FORM_SUBMIT_ERROR, NEW_SEARCH} from "./GoogleAnalytics";
import {buildIntroTextFromSearch} from "../utils/IntroText";

export type SubmitFunction = (values: GiftFormValues) => void

function parseFromSearch(search: string): Partial<GiftFormValues> {
    const p = new URLSearchParams(search);
    const genderParam = p.get("gender");
    const relationshipParam = p.get("relationship");
    const occasionParam = p.get("occasion");

    let gender: Gender | undefined = undefined;
    if (genderParam) {
        const normalized = genderParam.trim().toUpperCase();
        // Akzeptiere übliche Schreibweisen; bei unbekanntem Wert: MALE
        if (IsGender(normalized)) {
            gender = ToGender(genderParam)
        } else {
            gender = Gender.MALE;
        }
    }

    let relationship: string | undefined = undefined;
    if (relationshipParam) {
        const normalized = relationshipParam.trim().toUpperCase();
        // Akzeptiere übliche Schreibweisen; bei unbekanntem Wert: MALE
        if (IsRelationship(normalized)) {
            relationship = normalized
        }
    }

    let occasion: string | undefined = undefined;
    if (occasionParam) {
        const normalized = occasionParam.trim().toUpperCase();
        // Akzeptiere übliche Schreibweisen; bei unbekanntem Wert: MALE
        if (IsOccasion(normalized)) {
            occasion = normalized
        }
    }

    // Hobbies: parameter ist eine string array
    // Unterstützt sowohl ?hobbies=a&hobbies=b als auch ?hobbies=a,b
    let hobbies: string[] | undefined = undefined;
    const hobbiesMultiParam = p.getAll("hobbies"); // mehrere Query-Parameter
    if (hobbiesMultiParam && hobbiesMultiParam.length > 0) {
        hobbies = hobbiesMultiParam
            .flatMap(v => v.split(","))
            .map(s => s.trim())
            .filter(Boolean);
    } else {
        const hobbiesSingleParam = p.get("hobbies");
        if (hobbiesSingleParam) {
            hobbies = hobbiesSingleParam
                .split(",")
                .map(s => s.trim())
                .filter(Boolean);
        }
    }

    return {
        ...(hobbies && hobbies.length > 0 ? {hobbies} : {}),
        ...(occasion !== undefined ? {occasion: occasion} : {}),
        ...(relationship !== undefined ? {relationship: relationship} : {}),
        ...(gender !== undefined ? {gender: gender.toString()} : {}),
    };
}

// ... imports ...
const DEFAULT_GIFT_FORM: GiftFormValues = {
    age: 20,
    gender: Gender.MALE,
    relationship: GetEnumKeyByEnumValue(Relationship, Relationship.PARTNER),
    occasion: GetEnumKeyByEnumValue(Occasion, Occasion.CHRISTMAS),
    hobbies: []
};

const GiftPage = () => {
    const [showSuggestions, setShowSuggestions] = React.useState<boolean>(false);
    const [suggestions, setSuggestions] = React.useState<GiftResponse[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [showError, setShowError] = React.useState<boolean>(false);

    const eventTracker = useAnalyticsEventTracker("GiftPage");

    const text = buildIntroTextFromSearch(window.location.search)
    console.log(text)

    const giftFormDefaults = React.useMemo(
        () => ({...DEFAULT_GIFT_FORM, ...parseFromSearch(window.location.search)}),
        [window.location.search]
    );

    const onSubmit = (formValues: GiftFormValues) => {
        eventTracker(FORM_SUBMIT)

        console.log("formValues", formValues)
        setIsLoading(true)
        api.getGiftSuggestions(formValues).then(response => {
            setIsLoading(false)
            setShowSuggestions(true);
            if (response.status >= 200 && response.status < 300) {
                return response.data
            } else {
                setShowError(true)
                eventTracker(FORM_SUBMIT_ERROR)

                let errorMessage = "Server can't be reached!";
                if (response.status) {
                    errorMessage += " Status:" + response.status;
                }
                throw new Error(errorMessage)
            }
        }).then((data: GiftResponse[]) => {
            setSuggestions(data)
        }).catch((error) => {
            setShowError(true)
            setIsLoading(false)
            eventTracker(FORM_SUBMIT_ERROR)
        })
    }

    const newSearch = () => {
        setShowSuggestions(false)
        setSuggestions([])
        setShowError(false)
        setIsLoading(false)
        eventTracker(NEW_SEARCH)
    }

    var errorView = () => {
        return (<Grid xs={12} sx={{pt: 5}}>
                <h3>Oooops, hier ist etwas schiefgelaufen.</h3>
                <Button variant="contained" onClick={newSearch}>Neue Suche starten</Button>
            </Grid>
        )
    }
    return (
        <>
            <Box sx={{p: {xs: 1, md: 5}}}>
                <Container maxWidth="md">
                    <Grid2 container rowSpacing={5}>
                        <Grid2 xs={12} md={3} display="flex" alignItems="center" justifyContent="center">
                            <Box
                                component="img"
                                sx={{
                                    height: 80,
                                    // width: 350,
                                    maxHeight: {xs: 233, md: 167},
                                    // maxWidth: { xs: 350, md: 250 },
                                }}
                                alt="The house from the offer."
                                src="/gift.png"
                            />
                        </Grid2>
                        <Grid2 xs={12} md={9} display="flex" alignItems="center">
                            <Typography variant="h3" component="h1" align="center">
                                Geschenke für jeden Anlass!
                            </Typography>
                        </Grid2>
                        {!showSuggestions && !isLoading &&
                        <Grid2 xs={12}>
                            <Typography variant="h5" gutterBottom align="center" paragraph={true}>
                                {text}
                            </Typography>
                        </Grid2>
                        }
                        {showSuggestions &&
                            <Grid2 xs={12}>
                                <Typography variant="h5" gutterBottom align="center" paragraph={true}>
                                    Hier sind {suggestions.length} Geschenkideen, die genau zu deiner Auswahl passen
                                </Typography>
                            </Grid2>
                        }
                    </Grid2>
                </Container>
            </Box>
            <Paper square={false} sx={{p: {xs: 1, md: 5}}}>
                {!showError && !showSuggestions && !isLoading &&
                    <GiftForm onSubmit={onSubmit} defaults={giftFormDefaults}/>}
                {isLoading && !showError &&
                    <Box sx={{display: 'flex', justifyContent: 'center'}}><CircularProgress size="6rem"/> </Box>}
                {showSuggestions && !isLoading && <GiftSuggestions suggestions={suggestions} newSearch={newSearch}/>}
                {showError && !isLoading && errorView()}
            </Paper>
        </>
    )
        ;
}

export default GiftPage;